import { startTransition, useMemo, useState } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { apiClient, getApiFailure, type GrievanceCaseRecord } from "@/lib/api/client"

type GrievanceCaseType =
  | "grievance"
  | "correction_help"
  | "withdrawal_help"
  | "deletion_follow_up"
  | "guardian_approval_help"

interface PublicGrievancePageProps {
  initialCaseType?: string | null
  initialReason?: string | null
  initialSourceSurface?: string | null
  initialTag?: string | null
}

interface GrievanceFormState {
  caseType: GrievanceCaseType
  description: string
  requesterEmail: string
  requesterName: string
  requesterPhone: string
  tag: string
}

interface BannerState {
  message: string
  tone: "error" | "info" | "success"
}

const CASE_TYPE_OPTIONS: Array<{
  description: string
  helper: string
  value: GrievanceCaseType
}> = [
  {
    value: "grievance",
    description: "Raise a complaint about handling, delays, or an unresolved support issue.",
    helper: "General grievance",
  },
  {
    value: "correction_help",
    description: "Request tracked help when a profile change or correction cannot be completed directly.",
    helper: "Correction help",
  },
  {
    value: "withdrawal_help",
    description: "Ask for manual help with consent withdrawal or account suspension follow-up.",
    helper: "Withdrawal help",
  },
  {
    value: "deletion_follow_up",
    description: "Follow up on a deletion or erasure request that needs operator review.",
    helper: "Deletion follow-up",
  },
  {
    value: "guardian_approval_help",
    description: "Get help when a minor registration is blocked on guardian approval or verification.",
    helper: "Guardian approval help",
  },
]

const INPUT_CLASS =
  "mt-2 w-full rounded-2xl border border-border bg-background/90 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-primary focus:ring-4 focus:ring-primary/15"
const TEXTAREA_CLASS = `${INPUT_CLASS} min-h-36 resize-y`
const LABEL_CLASS = "text-sm font-medium text-foreground"
const HELP_CLASS = "mt-2 text-xs leading-5 text-muted-foreground"

function normalizeCaseType(value: string | null | undefined): GrievanceCaseType {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")

  return CASE_TYPE_OPTIONS.some((option) => option.value === normalized)
    ? (normalized as GrievanceCaseType)
    : "grievance"
}

function createInitialFormState(
  initialTag?: string | null,
  initialCaseType?: string | null
): GrievanceFormState {
  return {
    caseType: normalizeCaseType(initialCaseType),
    description: "",
    requesterEmail: "",
    requesterName: "",
    requesterPhone: "",
    tag: initialTag?.trim() || "",
  }
}

function titleizeCaseType(value: string | null | undefined) {
  const option = CASE_TYPE_OPTIONS.find((item) => item.value === value)
  if (option) {
    return option.helper
  }

  return "Grievance"
}

function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not scheduled"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function PublicGrievancePage({
  initialCaseType = null,
  initialReason = null,
  initialSourceSurface = null,
  initialTag = null,
}: PublicGrievancePageProps) {
  const [banner, setBanner] = useState<BannerState | null>(null)
  const [form, setForm] = useState<GrievanceFormState>(() =>
    createInitialFormState(initialTag, initialCaseType)
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submittedCase, setSubmittedCase] = useState<GrievanceCaseRecord | null>(
    null
  )

  const selectedCaseType = useMemo(
    () =>
      CASE_TYPE_OPTIONS.find((option) => option.value === form.caseType)
      || CASE_TYPE_OPTIONS[0],
    [form.caseType]
  )
  const isOwnerAuthManualReview =
    initialSourceSurface === "my_device_owner_auth"
    || initialReason === "owner_auth_unreachable_phone"

  function updateForm<K extends keyof GrievanceFormState>(
    key: K,
    value: GrievanceFormState[K]
  ) {
    setForm((current) => ({
      ...current,
      [key]: value,
    }))
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!form.description.trim()) {
      setBanner({
        message: "Describe the issue so the operator queue has enough detail to work the case.",
        tone: "error",
      })
      return
    }

    if (!form.requesterEmail.trim() && !form.requesterPhone.trim()) {
      setBanner({
        message: "Add a phone number or email so the operator team can respond to this case.",
        tone: "error",
      })
      return
    }

    setIsSubmitting(true)
    setBanner(null)

    try {
      const response = await apiClient.submitPublicGrievance({
        caseType: form.caseType,
        description: form.description.trim(),
        metadata: initialReason
          ? {
              intakeReason: initialReason,
            }
          : null,
        requesterEmail: form.requesterEmail.trim() || null,
        requesterName: form.requesterName.trim() || null,
        requesterPhone: form.requesterPhone.trim() || null,
        sourceSurface: initialSourceSurface || "public_grievance",
        tag: form.tag.trim() || null,
      })

      startTransition(() => {
        setSubmittedCase(response.data)
        setBanner({
          message:
            "Your request is now tracked in the operator queue. Keep the case details handy for follow-up.",
          tone: "success",
        })
      })
    } catch (error) {
      const failure = getApiFailure(error)

      setBanner({
        message:
          failure?.error
          || (error instanceof Error
            ? error.message
            : "We could not submit the grievance right now."),
        tone: "error",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <section className="grid gap-5">
      <Card className="shell-panel overflow-hidden rounded-4xl border-border/70">
        <CardHeader className="gap-4 bg-[radial-gradient(circle_at_top_left,rgba(236,201,75,0.18),transparent_28%),linear-gradient(135deg,rgba(18,37,63,0.05),rgba(18,37,63,0.01))]">
          <div className="flex flex-wrap items-center gap-3">
            <Badge variant="secondary">Tracked support</Badge>
            <Badge variant="outline">/grievance</Badge>
            {form.tag ? <Badge variant="outline">{form.tag}</Badge> : null}
          </div>
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.2fr)_minmax(18rem,0.8fr)]">
            <div className="grid gap-4">
              <CardTitle className="font-heading text-3xl leading-tight sm:text-4xl">
                Submit a tracked compliance or grievance request.
              </CardTitle>
              <CardDescription className="max-w-3xl text-base leading-7 text-muted-foreground">
                {isOwnerAuthManualReview
                  ? "Use this tracked manual-review path if you cannot access the approved OTP phone for /my-device. This creates an operator case for review; it is not a hidden OTP bypass."
                  : "Use this route for grievance review, correction help, withdrawal help, deletion follow-up, or guardian approval help. Each submission creates a persisted case for the operator queue instead of relying on email alone."}
              </CardDescription>
            </div>
            <div className="grid gap-3 text-sm leading-6 text-muted-foreground">
              {isOwnerAuthManualReview ? (
                <div className="rounded-3xl border border-amber-300/40 bg-amber-50 px-4 py-3 text-amber-950">
                  We will manually review the owner-access request for this tag and follow up through the tracked grievance queue.
                </div>
              ) : null}
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-3">
                First-response target: within 72 hours from submission.
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-3">
                Add a tag when you have it so operations can link the case to the right compliance record.
              </div>
              <div className="rounded-3xl border border-border/70 bg-background/70 px-4 py-3">
                Provide a phone number or email so the queue stays actionable.
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {banner ? <Banner banner={banner} /> : null}

      {submittedCase ? (
        <Card className="shell-panel rounded-4xl border-border/70">
          <CardHeader className="gap-3">
            <Badge variant="secondary">Case created</Badge>
            <CardTitle className="font-heading text-2xl">
              {titleizeCaseType(submittedCase.caseType)} is queued for operator review
            </CardTitle>
            <CardDescription className="text-base leading-7 text-muted-foreground">
              Reference <strong>{submittedCase.id}</strong> for follow-up. The queue entry
              is already persisted and visible to operations.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <FactCard label="Status" value={submittedCase.status || "open"} />
            <FactCard label="Created" value={formatDateTime(submittedCase.createdAt)} />
            <FactCard label="SLA due" value={formatDateTime(submittedCase.slaDueAt)} />
            <FactCard
              label="Linked record"
              value={submittedCase.tag || submittedCase.registrationComplianceId || "Not linked"}
            />
          </CardContent>
        </Card>
      ) : null}

      <Card className="shell-panel rounded-4xl border-border/70">
        <CardHeader className="px-0 pt-0">
          <CardTitle className="font-heading text-2xl">
            Choose the request type
          </CardTitle>
          <CardDescription className="text-base leading-7 text-muted-foreground">
            Pick the category that matches the operator action you need.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-3 px-0 pb-0 md:grid-cols-2 xl:grid-cols-3">
          {CASE_TYPE_OPTIONS.map((option) => {
            const isSelected = option.value === form.caseType

            return (
              <button
                key={option.value}
                className={
                  isSelected
                    ? "rounded-3xl border border-primary/40 bg-primary/5 px-4 py-4 text-left shadow-sm transition"
                    : "rounded-3xl border border-border/70 bg-background/70 px-4 py-4 text-left transition hover:border-primary/30 hover:bg-background"
                }
                type="button"
                onClick={() => updateForm("caseType", option.value)}
              >
                <div className="flex items-center justify-between gap-3">
                  <strong className="text-sm">{option.helper}</strong>
                  {isSelected ? <Badge variant="secondary">Selected</Badge> : null}
                </div>
                <p className="mt-3 text-sm leading-6 text-muted-foreground">
                  {option.description}
                </p>
              </button>
            )
          })}
        </CardContent>
      </Card>

      <Card className="shell-panel rounded-4xl border-border/70">
        <CardHeader className="px-0 pt-0">
          <CardTitle className="font-heading text-2xl">
            Share the case details
          </CardTitle>
          <CardDescription className="text-base leading-7 text-muted-foreground">
            {selectedCaseType.description}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-5 px-0 pb-0">
          <form className="grid gap-5" onSubmit={handleSubmit}>
            <div className="grid gap-4 md:grid-cols-2">
              <label className="block">
                <span className={LABEL_CLASS}>SafetyQR tag</span>
                <input
                  aria-label="SafetyQR tag"
                  className={INPUT_CLASS}
                  placeholder="Scan a tag or enter the printed code"
                  value={form.tag}
                  onChange={(event) => updateForm("tag", event.target.value)}
                />
                <span className={HELP_CLASS}>
                  Optional, but recommended for correction, withdrawal, deletion, or guardian approval follow-up.
                </span>
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Requester name</span>
                <input
                  aria-label="Requester name"
                  className={INPUT_CLASS}
                  placeholder="Who should the operator team reference?"
                  value={form.requesterName}
                  onChange={(event) => updateForm("requesterName", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Phone number</span>
                <input
                  aria-label="Phone number"
                  className={INPUT_CLASS}
                  placeholder="+1 555 123 4567"
                  value={form.requesterPhone}
                  onChange={(event) => updateForm("requesterPhone", event.target.value)}
                />
              </label>

              <label className="block">
                <span className={LABEL_CLASS}>Email address</span>
                <input
                  aria-label="Email address"
                  className={INPUT_CLASS}
                  placeholder="you@example.com"
                  type="email"
                  value={form.requesterEmail}
                  onChange={(event) => updateForm("requesterEmail", event.target.value)}
                />
                <span className={HELP_CLASS}>
                  Add at least one contact path: phone or email.
                </span>
              </label>
            </div>

            <label className="block">
              <span className={LABEL_CLASS}>What happened?</span>
              <textarea
                aria-label="What happened?"
                className={TEXTAREA_CLASS}
                placeholder="Describe the issue, what you already tried, and what outcome you need."
                value={form.description}
                onChange={(event) => updateForm("description", event.target.value)}
              />
            </label>

            <div className="flex flex-wrap gap-3">
              <Button disabled={isSubmitting} type="submit">
                {isSubmitting ? "Submitting..." : "Create tracked case"}
              </Button>
              {submittedCase ? (
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSubmittedCase(null)
                    setBanner(null)
                    setForm(createInitialFormState(form.tag, form.caseType))
                  }}
                >
                  Submit another request
                </Button>
              ) : null}
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  )
}

function FactCard(props: { label: string; value: string }) {
  return (
    <div className="rounded-3xl border border-border/70 bg-background/65 px-4 py-4">
      <div className="text-xs font-semibold tracking-[0.18em] text-muted-foreground uppercase">
        {props.label}
      </div>
      <div className="mt-3 text-sm leading-6">{props.value}</div>
    </div>
  )
}

function Banner({ banner }: { banner: BannerState }) {
  return (
    <div
      className={
        banner.tone === "error"
          ? "rounded-3xl border border-destructive/30 bg-destructive/8 px-4 py-3 text-sm leading-6 text-destructive"
          : banner.tone === "success"
            ? "rounded-3xl border border-emerald-300/40 bg-emerald-50 px-4 py-3 text-sm leading-6 text-emerald-900"
            : "rounded-3xl border border-border/70 bg-background/70 px-4 py-3 text-sm leading-6 text-foreground"
      }
    >
      {banner.message}
    </div>
  )
}
